import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { getQueries } from "@/requests/internal-products/get-queries";
import { executeQuery } from "@/requests/internal-products/execute-query";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Play, Download } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { toastApiError } from "@/lib/error-messages";

export function QueryGallery() {
  const { toast } = useToast();
  const [selectedQueryId, setSelectedQueryId] = useState<string>("");
  const [queryResults, setQueryResults] = useState<any[] | null>(null);
  const [queryName, setQueryName] = useState<string>("");

  // Fetch available queries
  const { data: queries, isLoading: loadingQueries } = useQuery({
    queryKey: ["/api/internal/products/queries"],
    queryFn: getQueries,
  });

  // Execute query mutation
  const executeQueryMutation = useMutation({
    mutationFn: executeQuery,
    onSuccess: (result) => {
      setQueryResults(result.rows);
      setQueryName(result.queryName);
      toast({
        title: "Query executed",
        description: `Returned ${result.rowCount} rows`,
      });
    },
    onError: (error) => {
      toastApiError(toast, error, "Query failed");
    },
  });

  const handleRunQuery = () => {
    if (!selectedQueryId) {
      toast({
        title: "No query selected",
        description: "Please select a query from the dropdown",
        variant: "destructive",
      });
      return;
    }
    executeQueryMutation.mutate(selectedQueryId);
  };

  const handleExportCsv = () => {
    if (!queryResults || queryResults.length === 0) return;

    // Convert to CSV
    const headers = Object.keys(queryResults[0]);
    const csvContent = [
      headers.join(","),
      ...queryResults.map((row) =>
        headers.map((h) => JSON.stringify(row[h] ?? "")).join(",")
      ),
    ].join("\n");

    // Download
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${queryName.toLowerCase().replace(/\s+/g, "-")}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const selectedQuery = queries?.find((q) => q.id === selectedQueryId);

  return (
    <div className="space-y-4">
      {/* Query Selector */}
      <div className="flex items-end gap-4">
        <div className="flex-1">
          <label className="text-sm font-medium mb-2 block">
            Select Query
          </label>
          <Select value={selectedQueryId} onValueChange={setSelectedQueryId}>
            <SelectTrigger>
              <SelectValue placeholder="Choose a query..." />
            </SelectTrigger>
            <SelectContent>
              {loadingQueries ? (
                <SelectItem value="loading" disabled>
                  Loading queries...
                </SelectItem>
              ) : (
                queries?.map((query) => (
                  <SelectItem key={query.id} value={query.id}>
                    {query.name}
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
          {selectedQuery && (
            <p className="text-sm text-muted-foreground mt-1">
              {selectedQuery.description}
            </p>
          )}
        </div>
        <Button
          onClick={handleRunQuery}
          disabled={!selectedQueryId || executeQueryMutation.isPending}
        >
          <Play className="mr-2 h-4 w-4" />
          {executeQueryMutation.isPending ? "Running..." : "Run Query"}
        </Button>
      </div>

      {/* Results */}
      {queryResults && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">
              Results: {queryName} ({queryResults.length} rows)
            </h3>
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportCsv}
              disabled={queryResults.length === 0}
            >
              <Download className="mr-2 h-4 w-4" />
              Export CSV
            </Button>
          </div>

          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  {queryResults.length > 0 &&
                    Object.keys(queryResults[0]).map((key) => (
                      <TableHead key={key} className="font-semibold">
                        {key}
                      </TableHead>
                    ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {queryResults.map((row, idx) => (
                  <TableRow key={idx}>
                    {Object.values(row).map((value: any, cellIdx) => (
                      <TableCell key={cellIdx}>
                        {value === null || value === undefined
                          ? "-"
                          : typeof value === "object"
                          ? JSON.stringify(value)
                          : String(value)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
}
